import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "../../components/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  UserCheck, Clock, MapPin, CalendarRange, ArrowUpRight, HelpCircle, Radio,
} from "lucide-react";

export default function VolunteerDashboard() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [volunteerRec, setVolunteerRec] = useState(null);

  useEffect(() => {
    api.get("/v2/events?limit=10").then((r) => setEvents(r.data.items || []));
    api.get("/v2/schedules?limit=5").then((r) => setSchedules(r.data.items || []));
    api.get("/v2/volunteers?limit=50").then((r) => {
      const mine = (r.data.items || []).find((v) => v.email === user?.email);
      setVolunteerRec(mine);
    });
  }, [user]);

  const assignedEvent = events.find((e) => e.id === volunteerRec?.event_id);
  const todaySchedule = schedules[0];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Volunteer Ops
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            You&apos;re making it happen, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="mt-2 text-slate-500 text-sm">
            Your shifts, your rooms, your checklists.
          </p>
        </motion.div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          <Card className="lg:col-span-7 bg-[#0A192F] text-white border-[#D4AF37]/30">
            <CardContent className="p-8">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                Your assignment
              </p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight">
                {assignedEvent?.title || "Awaiting event assignment"}
              </h2>
              <div className="mt-5 flex flex-wrap gap-5 text-sm">
                {assignedEvent && (
                  <>
                    <span className="flex items-center gap-2">
                      <CalendarRange className="h-4 w-4 text-[#D4AF37]" />
                      {new Date(assignedEvent.start_date).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#D4AF37]" />
                      {assignedEvent.location}
                    </span>
                  </>
                )}
                {volunteerRec?.track && (
                  <span className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-[#D4AF37]" />
                    Track: {volunteerRec.track}
                  </span>
                )}
                {volunteerRec?.shift && (
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#D4AF37]" />
                    {volunteerRec.shift}
                  </span>
                )}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/dashboard/volunteer/attendance">
                  <Button className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold" data-testid="mark-attendance-button">
                    <UserCheck className="h-4 w-4 mr-2" /> Mark attendance
                  </Button>
                </Link>
                {assignedEvent && (
                  <Link to={`/dashboard/events/${assignedEvent.id}`}>
                    <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white" data-testid="event-details-button">
                      Event details
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-5 bg-white border-slate-200">
            <CardContent className="p-6">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                Today&apos;s checklist
              </p>
              <ul className="mt-4 space-y-3 text-sm">
                {[
                  "Pick up volunteer badge & walkie from reg desk",
                  "Verify committee room setups (AV, name cards)",
                  "Check-in delegates at council entry",
                  "Assist chairs with tech & water refills",
                  "Close out venue at 19:00",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3 p-3 rounded-md bg-slate-50 border border-slate-200">
                    <span className="h-5 w-5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37] text-[#D4AF37] text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="text-slate-700">{t}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          <Card className="lg:col-span-7 bg-white border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold tracking-tight text-[#0A192F]">
                  Today&apos;s conference timeline
                </h3>
                <Radio className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="mt-5 divide-y divide-slate-100">
                {(todaySchedule?.items || []).slice(0, 8).map((it, i) => (
                  <div key={i} className="py-3 flex items-start gap-4" data-testid={`vol-timeline-${i}`}>
                    <p className="font-mono text-xs text-[#D4AF37] font-bold w-14">{it.time}</p>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#0A192F]">{it.title}</p>
                      {it.description && (
                        <p className="text-xs text-slate-500 mt-0.5">{it.description}</p>
                      )}
                    </div>
                    {it.room_id && (
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-[#0A192F] text-[#D4AF37]">
                        {it.room_id}
                      </span>
                    )}
                  </div>
                ))}
                {!todaySchedule && (
                  <p className="py-6 text-center text-slate-500 text-sm">No timeline published yet.</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-5 bg-white border-slate-200">
            <CardContent className="p-6">
              <HelpCircle className="h-5 w-5 text-[#D4AF37]" />
              <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                Need help?
              </p>
              <h3 className="mt-1 text-lg font-semibold tracking-tight text-[#0A192F]">
                Ping the Secretariat
              </h3>
              <p className="mt-3 text-sm text-slate-600">
                Open a support ticket for logistics issues, missing supplies or last-minute changes.
                Secretariat responds in minutes on show days.
              </p>
              <Button className="mt-5 bg-[#0F4C81] hover:bg-[#0A3860] w-full" data-testid="open-ticket-button" disabled>
                Open ticket (coming soon)
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
